import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { differenceInMinutes } from "date-fns";
import { useMemo } from "react";

import { formatDurationWithBreakParts } from "../../utils/formatDurationWithBreakParts";

interface UseShiftDurationWithBreakParams {
  startDate: Date;
  endDate: Date;
  workplaceId: string;
  requiresLunchBreak?: boolean;
  minutesElapsed?: number;
}

interface UseShiftDurationWithBreakResult {
  totalDurationInHours: number;
  breakDurationInMinutes: number;
  requiresBreak: boolean;
  durationParts: { duration: string; breakInfo: string };
  mandatoryBreakDurationInMinutes: number;
}

/**
 * Custom hook to calculate shift duration with break information
 *
 * This hook encapsulates the common pattern of:
 * 1. Fetching break configuration for a workplace
 * 2. Calculating total shift duration in hours
 * 3. Determining if break is required
 * 4. Formatting the duration display with break information
 */
export function useShiftDurationWithBreak(
  params: UseShiftDurationWithBreakParams
): UseShiftDurationWithBreakResult {
  const { startDate, endDate, workplaceId, requiresLunchBreak = false, minutesElapsed } = params;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  return useMemo(() => {
    // Calculate exact hours using differenceInMinutes for precision
    const totalDurationInHours = differenceInMinutes(endDate, startDate) / 60;
    const requiresBreak = requiresLunchBreak;
    const breakDurationInMinutes = requiresBreak ? mandatoryBreakDurationInMinutes : 0;
    const durationParts = formatDurationWithBreakParts({
      totalDurationInHours,
      breakDurationInMinutes,
      requiresBreak,
      minutesElapsed,
    });

    return {
      totalDurationInHours,
      breakDurationInMinutes,
      requiresBreak,
      durationParts,
      mandatoryBreakDurationInMinutes,
    };
  }, [startDate, endDate, requiresLunchBreak, mandatoryBreakDurationInMinutes, minutesElapsed]);
}
